import { useDispatch } from "react-redux"
import { bindActionCreators } from "redux"
import * as actionCreators from '../state/actionCreators'
import { StockState } from "../state/reducer"
import { useSelector } from "react-redux"
import { State } from "../../../state"
import { Stock } from "../state/types"
import { useManagerHooks } from "../../manager"
import { define, normalize } from "../../../utils/typeUtils"
import { Product, useProductLoaders, useProductHooks, useLotLoaders } from "../../products"
import { DataType } from "../../../tables"
import { StockAction, useFiltersHooks } from "../../../features/ui"
import { useSupplierLoaders } from "../../purchases"
import { RoutePath } from "../../../features/routes"
import { useStockLoaders } from "../loaders/stockLoaders"

export const useStockState = (): StockState => useSelector((state: State) => state.stockReducer)

export const useStockStateActions = () => bindActionCreators(actionCreators, useDispatch())

export const useStockHooks = () => {
    const { currentBranchId } = useManagerHooks()

    const { describeQuantity } = useProductHooks()
    const { search } = useFiltersHooks()

    const stockLoaders = useStockLoaders()
    const productLoaders = useProductLoaders()
    const supplierLoaders = useSupplierLoaders()
    const lotLoaders = useLotLoaders()

    const availableStock = async (
        product: Product,
        branchId: string,
        lotId?: string
    ) => {
        const productStocks = await stockLoaders().loadProductStocks(product.id, branchId)
        const stocks = lotId ? productStocks.filter(stock => stock.lotId === lotId) : productStocks
        return stocks.reduce((sum, stock) => sum + stock.availableUnits, 0)
    }

    const reservedStock = async (
        product: Product,
        branchId: string,
        lotId?: string
    ) => {
        const productStocks = await stockLoaders().loadProductStocks(product.id, branchId)
        const stocks = lotId ? productStocks.filter(stock => stock.lotId === lotId) : productStocks
        return stocks.reduce((sum, stock) => sum + stock.reservedUnits, 0)
    }

    const totalStock = async (
        product: Product,
        branchId: string,
        lotId?: string
    ) => {
        const available = await availableStock(product, branchId, lotId)
        const reserved = await reservedStock(product, branchId, lotId)
        return available + reserved
    }

    // Al vender sin stock el disponible queda negativo y el reservado incluye unidades que no existen físicamente.
    // Para mostrar: disponible mínimo 0, reservado solo lo físico, y lo faltante aparte como "vendido sin stock".
    const describeAvailableStock = async (
        product: Product,
        branchId: string,
        lotId?: string
    ) => {
        const units = await availableStock(product, branchId, lotId)
        return describeQuantity(Math.max(units, 0), product)
    }

    const describeReservedStock = async (
        product: Product,
        branchId: string,
        lotId?: string
    ) => {
        const available = await availableStock(product, branchId, lotId)
        const reserved = await reservedStock(product, branchId, lotId)
        const physicalReserved = Math.min(reserved, Math.max(available + reserved, 0))
        return describeQuantity(physicalReserved, product)
    }

    const describeTotalStock = async (
        product: Product,
        branchId: string,
        lotId?: string
    ) => {
        const units = await totalStock(product, branchId, lotId)
        return describeQuantity(Math.max(units, 0), product)
    }

    // Unidades vendidas sin stock que todavía no ingresaron; undefined si no hay
    const describeSoldWithoutStock = async (
        product: Product,
        branchId: string,
        lotId?: string
    ) => {
        const available = await availableStock(product, branchId, lotId)
        return available < 0 ? describeQuantity(-available, product) : undefined
    }

    const getNoLotStock = async (
        productId: string,
        branchId = currentBranchId
    ): Promise<Stock> => {
        const { loadProductStocks } = stockLoaders()
        const { loadProductBaseLot } = lotLoaders()
        const stocks = await loadProductStocks(productId, branchId)
        const baseLot = await loadProductBaseLot(productId)
        const noLotStock = stocks.find(stock => stock.lotId === define(baseLot).id)
        return define(noLotStock)
    }

    const searchStockList = async (
        stockList: Stock[],
        text = '',
        showAll = false
    ): Promise<Stock[]> => {
        const productIds = stockList.map(stock => stock.productId)
        const { loadProducts } = productLoaders()
        const products = await loadProducts(productIds)
        const supplierIds = products.map(product => product.supplierId)
        const { loadSuppliers } = supplierLoaders()
        const suppliers = await loadSuppliers(supplierIds)

        const valuesFn = (stockDT: DataType) => {
            const stock = stockDT as Stock
            const product = products.find(product => product.id === stock.productId)
            const { code, supplierCode, name, supplierId } = define(product)
            const supplier = suppliers.find(supplier => supplier.id === supplierId)
            return [
                code.toString(),
                supplierCode ? normalize(supplierCode) : '',
                normalize(name),
                normalize(define(supplier).name)
            ]
        }
        const sortFn = (stockDT_A: DataType, stockDT_B: DataType) => 
            (stockDT_B as Stock).availableUnits - (stockDT_A as Stock).availableUnits

        return search(stockList, valuesFn, sortFn, text, showAll) as Stock[]
    }

    const getStockActionRoute = (stockAction: StockAction) => stockAction === StockAction.ADD ? RoutePath.ADD : RoutePath.SUBSTRACT

    return {
        availableStock,
        reservedStock,
        totalStock,
        describeAvailableStock,
        describeReservedStock,
        describeTotalStock,
        describeSoldWithoutStock,
        getNoLotStock,
        searchStockList,
        getStockActionRoute
    }
}
